import { Box, CircularProgress, Divider, Typography } from '@mui/material'
import { IconCurrencyDollar, IconPercentage } from '@tabler/icons-react'
import { FormProvider, useForm } from 'react-hook-form'
import { useEffect } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { toast } from 'react-toastify'
import { Dialog } from '~/components'
import { Button } from '~/components/Form/Button'
import { Checkbox } from '~/components/Form/Checkbox'
import { Form } from '~/components/Form/Form'
import { Input } from '~/components/Form/Input'
import { useReportExternalPaymentMutation } from '~/graphql/types'
import { transformMoney } from '~/utils'
export type InvoiceReportModalProps = {
  isVisible: boolean
  toggleModal: () => void
  invoiceId: string
  onInvoiceReportSuccess: () => void
}
type InvoiceReportForm = {
  invoiceId: string,
  paymentDate: string,
  paidAmountCents: number,
  finesAndInterestWereCharged: boolean,
  finesAmountCents: number,
  finesPercentage: number,
  interestAmountCents: number,
  interestPercentage: number,
  note: string
}
const schema = yup.object().shape({
  paymentDate: yup.string().required('Data de pagamento é obrigatória'),
  paidAmountCents: yup.number().typeError('Valor do pagamento é obrigatório').required('Valor do pagamento é obrigatório'),
  finesAndInterestWereCharged: yup.boolean().default(false),
  finesAmountCents: yup.number().when('finesAndInterestWereCharged', {
    is: true,
    then: (field) => field.typeError('Multa é obrigatória').required('Multa é obrigatória'),
    otherwise: (field) => field.nullable()
  }),
  finesPercentage: yup.number().when('finesAndInterestWereCharged', {
    is: true,
    then: (field) => field.typeError('Percentual da multa é obrigatório').required('Percentual da multa é obrigatório'),
    otherwise: (field) => field.nullable()
  }),
  interestAmountCents: yup.number().when('finesAndInterestWereCharged', {
    is: true,
    then: (field) => field.typeError('Juros é obrigatório').required('Juros é obrigatório'),
    otherwise: (field) => field.nullable()
  }),
  interestPercentage: yup.number().when('finesAndInterestWereCharged', {
    is: true,
    then: (field) => field.typeError('Percentual dos juros é obrigatório').required('Percentual dos juros é obrigatório'),
    otherwise: (field) => field.nullable()
  }),
  note: yup.string().nullable()
})
export const InvoiceReportModal: React.FC<InvoiceReportModalProps> = ({ isVisible, toggleModal, invoiceId, onInvoiceReportSuccess }) => {
  const methods = useForm<InvoiceReportForm>({
    mode: 'onChange',
    resolver: yupResolver(schema)
  })
  const { formState: { isValid }, watch } = methods
  const finesAndInterestWereCharged = watch('finesAndInterestWereCharged')
  const [reportExternalPayment, { loading: reportExternalPaymentLoading }] = useReportExternalPaymentMutation({
    onCompleted: () => {
      toast.success('Fatura marcada como paga')
      handleReset()
      onInvoiceReportSuccess()
      toggleModal()
    }
  })
  const onSubmit = methods.handleSubmit(async (data: InvoiceReportForm) => {
    try {
      await reportExternalPayment({
        variables: {
          params: {
            invoiceId,
            paymentDate: data.paymentDate,
            paidAmountCents: transformMoney(Number(data.paidAmountCents), 'toCents') || 0,
            finesAndInterestWereCharged: data.finesAndInterestWereCharged,
            finesAmountCents: data.finesAndInterestWereCharged ? transformMoney(Number(data.finesAmountCents), 'toCents') || 0 : null,
            finesPercentage: data.finesAndInterestWereCharged ? data.finesPercentage : null,
            interestAmountCents: data.finesAndInterestWereCharged ? transformMoney(Number(data.interestAmountCents), 'toCents') || 0 : null,
            interestPercentage: data.finesAndInterestWereCharged ? data.interestPercentage : null,
            note: data.note || null
          }
        }
      })
    } catch (error) {
      toast.error('Erro ao marcar fatura como paga')
    }
  })
  const handleReset = () => {
    methods.reset({
      paymentDate: '',
      paidAmountCents: null as any,
      finesAndInterestWereCharged: false,
      finesAmountCents: null as any,
      finesPercentage: null as any,
      interestAmountCents: null as any,
      interestPercentage: null as any,
      note: ''
    })
  }
  useEffect(() => {
    if (!isVisible) {
      handleReset()
    }
  }, [isVisible])
  return (
    <Dialog
      isVisible={isVisible}
      title={!reportExternalPaymentLoading ? 'Marcar fatura negociada como paga' : ''}
      onClose={toggleModal}
    >
      {reportExternalPaymentLoading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', margin: '5rem 0' }}>
          <CircularProgress size={100} color='inherit' />
        </Box>
      )}
      {!reportExternalPaymentLoading && invoiceId && (
        <>
          <Divider />
          <Box sx={{ display: 'flex', flexFlow: 'column', gap: '2rem', padding: '2rem 0' }}>
            <Typography>Preencha os campos abaixo para marcar a fatura como paga:</Typography>
            <FormProvider {...methods}>
              <Form onSubmit={onSubmit}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                    <Input
                      type='date'
                      name='paymentDate'
                      label='Data de pagamento'
                    />
                    <Input
                      name='paidAmountCents'
                      label='Valor do pagamento'
                      mask='BRL'
                      icons={{
                        end: {
                          element: <IconCurrencyDollar />
                        }
                      }}
                    />
                    <Box display='flex' flexDirection='row' alignItems='center' gap='5px'>
                      <Checkbox name='finesAndInterestWereCharged' />
                      <Typography>Foram cobrados juros e multa</Typography>
                    </Box>
                    {finesAndInterestWereCharged && (
                      <>
                        <Box sx={{ display: 'flex', gap: '1rem' }}>
                          <Input
                            name='finesAmountCents'
                            label='Multa'
                            mask='BRL'
                            icons={{
                              end: {
                                element: <IconCurrencyDollar />
                              }
                            }}
                          />
                          <Input
                            name='finesPercentage'
                            label='Percentual da multa'
                            mask='percentage'
                            icons={{
                              end: {
                                element: <IconPercentage />
                              }
                            }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', gap: '1rem' }}>
                          <Input
                            name='interestAmountCents'
                            label='Juros'
                            mask='BRL'
                            icons={{
                              end: {
                                element: <IconCurrencyDollar />
                              }
                            }}
                          />
                          <Input
                            name='interestPercentage'
                            label='Percentual dos juros'
                            mask='percentage'
                            icons={{
                              end: {
                                element: <IconPercentage />
                              }
                            }}
                          />
                        </Box>
                      </>
                    )}
                    <Input
                      multiline
                      minRows={3}
                      name='note'
                      label='Observações (opcional)'
                    />
                  </Box>
                  <Box>
                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', paddingTop: '2rem' }}>
                      <Button
                        onClick={() => {
                          toggleModal()
                          handleReset()
                        }}
                        color='secondary'
                        type='button'
                      >
                        Cancelar
                      </Button>
                      <Button
                        type='submit'
                        variant='contained'
                        disabled={reportExternalPaymentLoading || !isValid}
                      >
                        Salvar
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Form>
            </FormProvider>
          </Box>
        </>
      )}
    </Dialog>
  )
}
